import { useNavigate } from 'react-router-dom';
import { useTenantLocation } from '../../context/LocationContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { MapPin, Layers, Check, Plus, ChevronDown } from 'lucide-react';

/**
 * Dropdown in the header that lets a multi-location merchant switch the
 * active location. Selection persists per-user (see LocationContext).
 *
 * Hidden when the tenant has no locations at all (they'll be prompted to
 * connect UnTill first).
 */
export const LocationSwitcher = () => {
  const navigate = useNavigate();
  const { locations, currentLocationId, currentLocation, setCurrentLocationId } = useTenantLocation();

  if (!locations || locations.length === 0) {
    // No locations yet — take the merchant to the Locations page so they can add one.
    return (
      <Button
        variant="outline"
        onClick={() => navigate('/locations')}
        className="h-9 gap-2 border-slate-700 text-slate-300 hover:text-white hover:border-blue-500/60"
        data-testid="location-switcher-empty"
      >
        <Plus className="w-4 h-4" />
        <span className="hidden md:inline">Add location</span>
      </Button>
    );
  }

  const isAll = !currentLocationId;
  const activeLabel = isAll ? 'All Locations' : (currentLocation?.location_name || 'Location');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-9 gap-2 border-slate-700 bg-slate-900/60 text-slate-100 hover:bg-slate-800 hover:text-white min-w-0 sm:min-w-[180px] justify-between px-2.5 sm:px-4"
          data-testid="location-switcher-btn"
        >
          <span className="flex items-center gap-2 truncate">
            {isAll ? <Layers className="w-4 h-4 text-blue-300" /> : <MapPin className="w-4 h-4 text-blue-300" />}
            <span className="truncate max-w-[96px] sm:max-w-[140px] text-sm">{activeLabel}</span>
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 bg-slate-900 border-slate-800" data-testid="location-switcher-menu">
        <DropdownMenuLabel className="text-slate-500 text-xs uppercase tracking-wider">Viewing</DropdownMenuLabel>

        <DropdownMenuItem
          onClick={() => setCurrentLocationId(null)}
          className="text-slate-200 focus:text-white focus:bg-slate-800 gap-2"
          data-testid="location-option-all"
        >
          <Layers className="w-4 h-4 text-blue-300 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm">All locations</div>
            <div className="text-[10px] text-slate-500">Combined totals across every venue</div>
          </div>
          {isAll && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-slate-800" />
        <DropdownMenuLabel className="text-slate-500 text-xs uppercase tracking-wider">Locations</DropdownMenuLabel>

        {locations.map((loc) => {
          const selected = currentLocationId === loc.location_id;
          return (
            <DropdownMenuItem
              key={loc.location_id}
              onClick={() => setCurrentLocationId(loc.location_id)}
              className="text-slate-200 focus:text-white focus:bg-slate-800 gap-2"
              data-testid={`location-option-${loc.location_id}`}
            >
              <MapPin className="w-4 h-4 text-blue-300 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate flex items-center gap-1.5">
                  {loc.location_name}
                  {loc.is_default && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">DEFAULT</span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 truncate">
                  {loc.verified ? (loc.verified_identifier || loc.site_label || loc.tenant_id) : 'not verified yet'} · {loc.counts?.products ?? 0} products · {loc.counts?.sales ?? 0} sales
                </div>
              </div>
              {selected && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator className="bg-slate-800" />
        <DropdownMenuItem
          onClick={() => navigate('/locations')}
          className="text-blue-300 focus:text-white focus:bg-blue-500/10 gap-2"
          data-testid="manage-locations-link"
        >
          <Plus className="w-4 h-4" />
          Manage locations
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
